import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useFilterParam } from "../hooks/useFilterParam";
import PropTypes from "prop-types";

const FilterContext = createContext(null);

export const FilterProvider = ({ facetConfigs, products, facets, children }) => {
	// --- Mobile (drawer) ---
	const [isOpen, setIsOpen] = useState(false);
	const openDrawer = useCallback(() => setIsOpen(true), []);
	const closeDrawer = useCallback(() => setIsOpen(false), []);

	// --- Desktop (popover) : un seul ouvert ---
	const [activeFacetId, setActiveFacetId] = useState(null);
	const openFacet = useCallback(id => setActiveFacetId(id), []);
	const closeFacet = useCallback(() => setActiveFacetId(null), []);
	const toggleFacet = useCallback(id => {
		setActiveFacetId(prev => (prev === id ? null : id));
	}, []);

	const filterFields = useMemo(() => {
		if (facets) {
			return Object.keys(facets);
		}

		return facetConfigs?.map(config => config.field);
	}, [facetConfigs, facets]);

	const { filters, setFilters, resetFilters, selectedFiltersCount } = useFilterParam(
		filterFields
	);

	const value = useMemo(
		() => ({
			isOpen,
			openDrawer,
			closeDrawer,
			activeFacetId,
			openFacet,
			closeFacet,
			toggleFacet,
			products,
			facetConfigs,
			facets,
			filters,
			setFilters,
			resetFilters,
			selectedFiltersCount,
			filterFields,
		}),
		[
			isOpen,
			openDrawer,
			closeDrawer,
			activeFacetId,
			openFacet,
			closeFacet,
			toggleFacet,
			products,
			facetConfigs,
			facets,
			filters,
			setFilters,
			resetFilters,
			selectedFiltersCount,
			filterFields,
		]
	);

	return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};

FilterProvider.propTypes = {
	facetConfigs: PropTypes.array.isRequired,
	facets: PropTypes.object,
	products: PropTypes.array.isRequired,
	children: PropTypes.node,
};

export const useFilterContext = () => {
	const ctx = useContext(FilterContext);

	if (!ctx) {
		throw new Error("useFilterContext must be used within <FilterProvider>");
	}

	return ctx;
};
